import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
} from "antd";

import { ClearOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import {
  getProveedores,
  createProveedor,
  updateProveedor,
  deleteProveedor,
} from "../../services/entregas/servicesProveedoresPO";
import Header166 from "../header/header";
import "./proveedoresPO.css";
import ProveedorForm from "./ProveedorForm";


import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";

const { Option } = Select;

const ProveedoresPO = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProveedor, setCurrentProveedor] = useState(null);
  const [form] = Form.useForm();

  const [searchNombre, setSearchNombre] = useState("");
  const [filterIdent, setFilterIdent] = useState(null);

  //Control de Permisos
  const appName = "proveedores";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionInsert = permissions.priv_insert === "Y";

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // cargar lista
  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getProveedores();

      if (resp.status === 200) {
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setProveedores(resp.data);
        } else {
          setProveedores([]);
          message.info("No hay proveedores registrados.");
        }
      } else {
        setProveedores([]);
        message.error(resp.message || "No se pudieron cargar los proveedores.");
      }
    } catch (error) {
      setProveedores([]);
      message.error(
        "Error al cargar proveedores. Por favor, inténtalo más tarde."
      );
      console.error("fetchData error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // lista identificaciones únicas para filtro
  const idents = useMemo(() => {
    if (!Array.isArray(proveedores)) return [];
    return Array.from(new Set(proveedores.map((p) => p.identificacion)));
  }, [proveedores]);

  // datos filtrados
  const filtered = useMemo(() => {
    return proveedores.filter((p) => {
      const matchName = p.nombre
        .toLowerCase()
        .includes(searchNombre.toLowerCase());
      const matchIdent = filterIdent ? p.identificacion === filterIdent : true;
      return matchName && matchIdent;
    });
  }, [proveedores, searchNombre, filterIdent]);

  // limpiar filtros
  const clearFilters = () => {
    setSearchNombre("");
    setFilterIdent(null);
  };

  // abrir modal
  const openModal = (proveedor) => {
    setIsEditing(!!proveedor);
    setCurrentProveedor(proveedor || null);
    form.resetFields();
    if (proveedor) {
      form.setFieldsValue({
        nombre: proveedor.nombre,
        identificacion: proveedor.identificacion,
        direccion: proveedor.direccion,
        correo: proveedor.correo,
        telefono: proveedor.telefono,
      });
    }
    setModalVisible(true);
  };

  // submit formulario
  const onFinish = async (values) => {
    try {
      const resp = isEditing
        ? await updateProveedor(currentProveedor.oid, values)
        : await createProveedor(values);

      switch (resp.status) {
        case 201:
          message.success(resp.message || "Proveedor creado");
          setModalVisible(false);
          fetchData();
          break;
        case 200:
          message.success(resp.message || "Proveedor actualizado");
          setModalVisible(false);
          fetchData();
          break;
        case 400:
          message.warning(resp.message || "Datos inválidos");
          break;
        case 409:
          message.error(
            resp.message || "Proveedor duplicado o restricción violada"
          );
          break;
        case 500:
          message.error(resp.message || "Error interno del servidor");
          break;
        default:
          message.info(resp.message || `Respuesta: ${resp.status}`);
      }
    } catch (error) {
      message.error("Error al guardar el proveedor");
      console.error(error);
    }
  };

  // eliminar
  const handleDelete = async (oid) => {
    try {
      const resp = await deleteProveedor(oid);
      switch (resp.status) {
        case 200:
          message.success(resp.message || "Proveedor eliminado");
          fetchData();
          break;
        case 400:
          message.warning(resp.message || "Petición inválida");
          break;
        case 404:
          message.error(resp.message || "Proveedor no encontrado");
          break;
        case 500:
          message.error(resp.message || "Error interno del servidor");
          break;
        default:
          message.info(resp.message || `Respuesta: ${resp.status}`);
      }
    } catch (error) {
      message.error("Error al eliminar el proveedor");
      console.error(error);
    }
  };

  const columns = [
    {
      title: "Acciones",
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <div className="proveedores-po-actions">
          {permissionUpdate && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
              title="Editar"
            />
          )}
          {permissionDelete && (
            <Popconfirm
              title="¿Eliminar proveedor?"
              onConfirm={() => handleDelete(record.oid)}
              okText="Sí"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                title="Eliminar"
              />
            </Popconfirm>
          )}
        </div>
      ),
    },
    { title: "OID", dataIndex: "oid", key: "oid", width: 50, align: "center" },
    { title: "Nombre", dataIndex: "nombre", key: "nombre", width: 300 },
    {
      title: "Identificación",
      align: "center",
      dataIndex: "identificacion",
      key: "identificacion",
      width: 120,
    },
    {
      title: "Dirección",
      dataIndex: "direccion",
      key: "direccion",
      width: 300,
    },
    { title: "Correo", dataIndex: "correo", key: "correo", width: 150 },
    {
      title: "Teléfono",
      align: "center",
      dataIndex: "telefono",
      key: "telefono",
      width: 150,
    },
  ];

  return (
    <div className="container">
      <Header166 type="proveedoresPO" />
      <div className="proveedores-po-filters">
        <Input
          placeholder="Buscar por nombre"
          value={searchNombre}
          onChange={(e) => setSearchNombre(e.target.value)}
          style={{ width: 200, marginRight: 8 }}
        />
        <Select
          placeholder="Filtrar por ID"
          allowClear
          showSearch
          style={{ width: 200, marginRight: 8 }}
          onChange={(val) => setFilterIdent(val)}
          value={filterIdent}
          filterOption={(input, option) =>
            option?.children
              ?.toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {idents.map((id) => (
            <Option key={id} value={id}>
              {id}
            </Option>
          ))}
        </Select>

        <Button
          icon={<ClearOutlined />}
          onClick={clearFilters}
          style={{ marginRight: 8 }}
        >
          Limpiar
        </Button>
        {permissionInsert && (
          <Button type="primary" onClick={() => openModal(null)}>
            Nuevo Proveedor
          </Button>
        )}
      </div>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="oid"
        loading={loading}
        bordered
        size="small"
        scroll={{ y: 500, x: "max-content" }}
        pagination={{
          current: currentPage, // página actual
          pageSize: pageSize, // filas por página
          total: filtered.length, // total de registros
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      <ProveedorForm
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={onFinish}
        form={form}
        isEditing={isEditing}
        proveedoresOptions={proveedores}
      />
    </div>
  );
};

export default ProveedoresPO;
